import React, { useEffect, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { kasirNav } from '@/lib/navConfig';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, Info } from 'lucide-react';
import api, { fmtIDR, fmtDate, API } from '@/lib/api';

export default function KasirHistory() {
  const [txs, setTxs] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get('/transactions?limit=200').then(({ data }) => setTxs(data));
  }, []);

  const downloadPDF = async (tx) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/transactions/${tx.id}/receipt.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `receipt-${tx.invoice_no}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const pmColor = (pm) => {
    if (pm === 'tempo') return 'bg-amber-100 text-amber-700 hover:bg-amber-100';
    if (pm === 'cash') return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100';
    return 'bg-sky-100 text-sky-700 hover:bg-sky-100';
  };

  return (
    <AppShell nav={kasirNav} title="Riwayat Transaksi">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Salesman</TableHead>
                <TableHead>Bayar</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Sisa Tempo</TableHead>
                <TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {txs.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">Belum ada transaksi</TableCell></TableRow>
                ) : txs.map((t) => (
                  <TableRow key={t.id} data-testid={`history-row-${t.id}`}>
                    <TableCell className="font-mono text-xs">{t.invoice_no}</TableCell>
                    <TableCell>{fmtDate(t.created_at)}</TableCell>
                    <TableCell>{t.salesman_name}</TableCell>
                    <TableCell><Badge className={`uppercase text-[10px] ${pmColor(t.payment_method)}`}>{t.payment_method}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{fmtIDR(t.total)}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-800">
                      {t.payment_method === 'tempo' ? fmtIDR(t.remaining_amount || 0) : '—'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {t.payment_method === 'tempo' && (
                        <Button size="sm" variant="outline" onClick={() => setDetail(t)} data-testid={`tempo-detail-${t.id}`}><Info className="h-3.5 w-3.5 mr-1" />Detail</Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => downloadPDF(t)}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Detail Tempo Proyek</DialogTitle></DialogHeader>
          {detail && (
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span className="font-mono">{detail.invoice_no}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="tabular-nums font-semibold">{fmtIDR(detail.total)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">DP Dibayar</span><span className="tabular-nums text-emerald-700 font-semibold">{fmtIDR(detail.dp_amount || 0)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sisa Tempo</span><span className="tabular-nums text-amber-800 font-semibold">{fmtIDR(detail.remaining_amount || 0)}</span></div>
              <div className="border-t pt-2 space-y-1">
                <Row label="Skema" value="Proyek mengikuti termin" />
                <Row label="Staff PJ" value={detail.tempo_details?.staff_pj} />
                <Row label="Nama Proyek" value={detail.tempo_details?.project_name} />
                {detail.tempo_details?.location_kabupaten && <Row label="Kabupaten/Kota" value={detail.tempo_details.location_kabupaten} />}
                {detail.tempo_details?.location_desa && <Row label="Desa" value={detail.tempo_details.location_desa} />}
                {detail.tempo_details?.location_pj && <Row label="PJ Lokasi" value={detail.tempo_details.location_pj} />}
                {detail.tempo_details?.notes && <Row label="Catatan" value={detail.tempo_details.notes} />}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value || '-'}</span>
    </div>
  );
}
